export type ProjectStatus = "online" | "beta" | "internal" | "archived";
export type ProjectVisibility = "public" | "hidden" | "private";
export type ReviewState = "draft" | "needs_review" | "approved" | "needs_info" | "archived";

export type Project = {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  tags: string[];
  summary: string;
  description: string;
  features: string[];
  url: string;
  guideUrl?: string;
  imageTone: "cyan" | "amber" | "green" | "rose" | "violet" | "slate";
  status: ProjectStatus;
  visibility: ProjectVisibility;
  reviewState: ReviewState;
  featured: boolean;
  pinned: boolean;
  sortOrder: number;
  audience: string[];
  updatedAt: string;
};

export const statusLabels: Record<ProjectStatus, string> = {
  online: "已上線",
  beta: "測試中",
  internal: "內部使用",
  archived: "歷史作品"
};

export const projects: Project[] = [
  {
    id: "ai-literacy",
    title: "AI 素養冒險島",
    subtitle: "國小 AI 素養互動題庫與闖關系統",
    category: "AI 與教材設計",
    tags: ["AI素養", "題庫", "闖關", "教師後台"],
    summary: "結合題庫、徽章與教師管理的 AI 素養學習工具，適合課堂活動與研習示範。",
    description:
      "以學生任務、教師管理與題庫同步為核心，協助教師把 AI 素養議題轉成可操作的課堂體驗。",
    features: ["互動題庫", "學生帳號", "徽章任務", "教師管理"],
    url: "https://meow3.winnie.tw/",
    guideUrl: "https://meow3.winnie.tw/guide",
    imageTone: "cyan",
    status: "online",
    visibility: "public",
    reviewState: "approved",
    featured: true,
    pinned: true,
    sortOrder: 10,
    audience: ["教師", "學生", "研習講師"],
    updatedAt: "2026-09-21"
  },
  {
    id: "meetnotice",
    title: "研習等候簡報工具",
    subtitle: "研習開場前的資訊看板與等待畫面",
    category: "研習與現場工具",
    tags: ["研習", "等候畫面", "簡報", "分享"],
    summary: "將研習資訊、提示、QR Code 與視覺主題整理成可分享的等待畫面。",
    description:
      "讓講師在活動開始前就能呈現清楚資訊，也能用模板快速建立不同研習場次的開場畫面。",
    features: ["多種版型", "分享連結", "視覺主題", "手機適配"],
    url: "https://meetnotice.winnie.tw/",
    imageTone: "green",
    status: "online",
    visibility: "public",
    reviewState: "approved",
    featured: true,
    pinned: false,
    sortOrder: 20,
    audience: ["研習講師", "教師"],
    updatedAt: "2026-09-09"
  },
  {
    id: "worksheet-prompt",
    title: "學習單提示詞生成器",
    subtitle: "協助教師快速產出可貼到 AI 的學習單指令",
    category: "AI 與教材設計",
    tags: ["提示詞", "學習單", "108課綱", "國語"],
    summary: "將年級、領域、主題、文本與需求整理成高品質學習單提示詞。",
    description:
      "降低教師撰寫提示詞的門檻，讓 ChatGPT、Gemini、Claude 都能更穩定地產出可編修學習單。",
    features: ["欄位引導", "可複製提示詞", "書寫空間要求", "手機可用"],
    url: "https://worksheet.winnie.tw/",
    imageTone: "amber",
    status: "beta",
    visibility: "public",
    reviewState: "approved",
    featured: true,
    pinned: false,
    sortOrder: 30,
    audience: ["教師"],
    updatedAt: "2026-09-10"
  },
  {
    id: "campus-case",
    title: "校園事件簿",
    subtitle: "AI 與校園議題的情境式案例互動頁",
    category: "情境互動學習",
    tags: ["情境案例", "校園議題", "互動閱讀", "AI"],
    summary: "用章節式互動故事，引導學習者蒐集線索、判斷情境並完成案例任務。",
    description:
      "適合在研習或課堂中示範 AI 議題判讀、資料線索整理與事件推理流程。",
    features: ["章節任務", "線索收集", "完成紀錄", "手機閱讀"],
    url: "https://meow1.winnie.tw/",
    imageTone: "rose",
    status: "online",
    visibility: "public",
    reviewState: "approved",
    featured: false,
    pinned: false,
    sortOrder: 40,
    audience: ["教師", "學生"],
    updatedAt: "2026-09-15"
  },
  {
    id: "grad-life",
    title: "Grad Life",
    subtitle: "研究生活任務、行事曆與進度管理工具",
    category: "工具與展示",
    tags: ["任務管理", "行事曆", "進度整理", "生活規劃"],
    summary: "整合任務、時間區間、行事曆與訪客模式的個人管理 Web App。",
    description:
      "以實際研究與生活節奏為設計核心，支援任務時間、行事曆拖拉與訪客展示資料。",
    features: ["任務時間", "行事曆", "訂閱行程", "訪客展示"],
    url: "https://grad.winnie.tw/guest",
    imageTone: "slate",
    status: "beta",
    visibility: "public",
    reviewState: "approved",
    featured: false,
    pinned: false,
    sortOrder: 50,
    audience: ["個人管理", "展示"],
    updatedAt: "2026-09-15"
  },
  {
    id: "moonlit-inn",
    title: "魔法甜點村：星月旅店",
    subtitle: "遊戲化題庫與操作指南實驗",
    category: "情境互動學習",
    tags: ["RPG", "題庫", "遊戲化", "指南"],
    summary: "以奇幻旅店為主題的遊戲化學習與題庫操作流程展示。",
    description:
      "適合整理遊戲化學習規則、題庫替換流程與公開操作文件的視覺化示範。",
    features: ["題庫流程", "操作手冊", "遊戲化主題", "手機適配"],
    url: "https://moonlit.winnie.tw/",
    imageTone: "violet",
    status: "archived",
    visibility: "public",
    reviewState: "approved",
    featured: false,
    pinned: false,
    sortOrder: 60,
    audience: ["教師", "教材設計"],
    updatedAt: "2026-09-01"
  }
];

export const categories = [
  {
    name: "AI 與教材設計",
    description: "把 AI 素養、提示詞與教材產製整理成教師可直接使用的作品。"
  },
  {
    name: "研習與現場工具",
    description: "支援研習開場、活動引導、資訊展示與現場操作流程。"
  },
  {
    name: "情境互動學習",
    description: "用故事、任務、闖關與角色情境提升學習參與感。"
  },
  {
    name: "工具與展示",
    description: "整理個人管理、公開展示與可分享的數位工具入口。"
  }
];
